import { useEffect, useRef, useState } from "react";
import api from "../../core/api";

/**
 * WO-CLOSING-PHOTOS-0414 follow-up: Fetches authenticated binary resources
 * (images, files) through axios and exposes them as `blob:` URLs usable by
 * `<img src>` and `<a href>`.
 *
 * Why this is needed: the Maintenance WebApi file endpoints require the
 * `Authorization: Bearer ...` header (set by axios' request interceptor in
 * `core/api.ts`). Plain `<img src="...">` and `<a href="...">` tags don't send
 * custom headers — only cookies — so every request 401s and images render as
 * broken icons.
 *
 * Strategy:
 * 1. For each absolute URL, fire an authenticated axios GET with
 *    `responseType: "blob"`.
 * 2. Wrap the response in `URL.createObjectURL(blob)` → produces a
 *    `blob:https://...` URL that `<img>` and `<a download>` accept natively.
 * 3. Cache by URL to avoid re-fetching the same file within one component tree.
 * 4. Revoke all blob URLs on unmount (and when the input URL list changes) to
 *    prevent memory leaks.
 */

// Module-level cache shared across all hook instances. Same URL across multiple
// components (e.g. carousel + lightbox) reuses the same blob URL — critical
// because `<img>` inside the lightbox dialog would otherwise re-fetch.
interface CacheEntry {
  blobUrl: string;
  refCount: number;
}
const blobCache = new Map<string, CacheEntry>();
const inFlight = new Map<string, Promise<string>>();

const fetchBlob = async (sourceUrl: string): Promise<string> => {
  const existing = blobCache.get(sourceUrl);
  if (existing) {
    existing.refCount += 1;
    return existing.blobUrl;
  }

  const pending = inFlight.get(sourceUrl);
  if (pending) {
    const blobUrl = await pending;
    const entry = blobCache.get(sourceUrl);
    if (entry) entry.refCount += 1;
    return blobUrl;
  }

  const request = (async () => {
    // `api` already has baseURL = apiBaseUrl; these file URLs are absolute
    // (maintenanceApiBaseUrl), so we call with a fully-qualified URL and
    // override baseURL to empty to prevent concatenation issues.
    const response = await api.get<Blob>(sourceUrl, {
      responseType: "blob",
      baseURL: "",
    });
    const blobUrl = URL.createObjectURL(response.data);
    blobCache.set(sourceUrl, { blobUrl, refCount: 1 });
    return blobUrl;
  })();

  inFlight.set(sourceUrl, request);
  try {
    return await request;
  } finally {
    inFlight.delete(sourceUrl);
  }
};

const releaseBlob = (sourceUrl: string) => {
  const entry = blobCache.get(sourceUrl);
  if (!entry) return;
  entry.refCount -= 1;
  if (entry.refCount <= 0) {
    URL.revokeObjectURL(entry.blobUrl);
    blobCache.delete(sourceUrl);
  }
};

export interface AuthenticatedBlobState {
  /** Resolved blob: URLs in the same order as the input URLs. `null` entries
   *  mean still loading (or failed). */
  urls: (string | null)[];
  /** True while any of the requests is still in flight. */
  loading: boolean;
  /** Error message from the first failed fetch, if any. */
  error: string | null;
}

/**
 * Fetch a list of authenticated URLs and return blob: URLs in the same order.
 * The order is preserved so callers can index-align metadata (file names,
 * sizes, etc.) with the resolved blobs.
 */
export function useAuthenticatedBlobUrls(
  sourceUrls: string[]
): AuthenticatedBlobState {
  const [urls, setUrls] = useState<(string | null)[]>(() =>
    sourceUrls.map(() => null)
  );
  const [loading, setLoading] = useState<boolean>(sourceUrls.length > 0);
  const [error, setError] = useState<string | null>(null);

  // Keep a stable key so we don't re-fetch when the input array identity
  // changes but the content doesn't.
  const key = sourceUrls.join("|");
  const trackedUrlsRef = useRef<string[]>([]);

  useEffect(() => {
    let cancelled = false;

    // Release previously tracked URLs before fetching the new set.
    trackedUrlsRef.current.forEach(releaseBlob);
    trackedUrlsRef.current = [];

    if (sourceUrls.length === 0) {
      setUrls([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    setUrls(sourceUrls.map(() => null));

    const resolvedSlots: (string | null)[] = sourceUrls.map(() => null);
    let firstError: string | null = null;

    Promise.all(
      sourceUrls.map((u, i) =>
        fetchBlob(u)
          .then((blobUrl) => {
            if (cancelled) {
              // Fetched after unmount — decrement ref immediately.
              releaseBlob(u);
              return;
            }
            trackedUrlsRef.current.push(u);
            resolvedSlots[i] = blobUrl;
            setUrls([...resolvedSlots]);
          })
          .catch((e: any) => {
            if (!firstError) {
              firstError = e?.message || "Failed to load image";
            }
          })
      )
    ).then(() => {
      if (cancelled) return;
      setLoading(false);
      if (firstError) setError(firstError);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Release all tracked blob refs on unmount.
  useEffect(() => {
    return () => {
      trackedUrlsRef.current.forEach(releaseBlob);
      trackedUrlsRef.current = [];
    };
  }, []);

  return { urls, loading, error };
}

/**
 * Convenience helper for the "download one file" interaction — fetches the
 * file via authenticated axios and triggers a browser download with the given
 * file name. Doesn't need React state because it's a fire-and-forget action.
 *
 * Returns a Promise that resolves when the download link has been clicked.
 * Errors propagate so the caller can surface them via a toast.
 */
export async function downloadAuthenticatedFile(
  sourceUrl: string,
  suggestedFileName: string
): Promise<void> {
  const response = await api.get<Blob>(sourceUrl, {
    responseType: "blob",
    baseURL: "",
  });
  const blobUrl = URL.createObjectURL(response.data);
  try {
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = suggestedFileName || "download";
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    // Revoke on next tick — giving the browser time to start the download.
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  }
}
